import React, { useEffect, useMemo, useState } from "react";
import api from "../../api/client";
import Header from "../../components/layout/Header";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Archive, ArrowRight, Building2, FilterX, MapPin, Pencil, Plus, RotateCcw, Search, ShieldCheck, Users } from "lucide-react";
import { Link } from "react-router-dom";
import { formatCNPJ } from "../../utils/format";
import EmptyState from "../../components/common/EmptyState";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../../components/ui/dialog";
import { useAuth } from "../../context/AuthContext";

export default function ClientsList() {
  const { isAdmin, can } = useAuth();
  const canCreate = isAdmin || can("can_create_client");
  const [clients, setClients] = useState([]);
  const [search, setSearch] = useState("");
  const [cityFilter, setCityFilter] = useState("todas");
  const [riskFilter, setRiskFilter] = useState("todos");
  const [statusFilter, setStatusFilter] = useState("ativos");
  const [loading, setLoading] = useState(true);
  const [confirmArchive, setConfirmArchive] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/clients", { params: { include_archived: isAdmin } });
      setClients(r.data);
    } catch (e) { toast.error("Falha ao carregar clientes"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [isAdmin]);

  const archiveClient = async () => {
    if (!confirmArchive) return;
    try {
      await api.post(`/clients/${confirmArchive.id}/archive`);
      toast.success("Cliente arquivado. O histórico foi preservado.");
      setConfirmArchive(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Falha ao arquivar cliente"); }
  };

  const restoreClient = async (client) => {
    try {
      await api.post(`/clients/${client.id}/restore`);
      toast.success("Cliente restaurado");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Falha ao restaurar cliente"); }
  };

  const cities = useMemo(() => Array.from(new Set(
    clients.map((c) => [c.municipio || c.cidade, c.uf].filter(Boolean).join("/")).filter(Boolean)
  )).sort((a, b) => a.localeCompare(b, "pt-BR")), [clients]);

  const risks = useMemo(() => Array.from(new Set(
    clients.map((c) => String(c.grau_risco || "").trim()).filter(Boolean)
  )).sort((a, b) => Number(a) - Number(b)), [clients]);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return clients.filter((c) => {
      const archived = !!c.archived_at;
      if (statusFilter === "ativos" && archived) return false;
      if (statusFilter === "arquivados" && !archived) return false;
      const city = [c.municipio || c.cidade, c.uf].filter(Boolean).join("/");
      if (cityFilter !== "todas" && city !== cityFilter) return false;
      if (riskFilter !== "todos" && String(c.grau_risco || "") !== riskFilter) return false;
      if (!term) return true;
      const hay = [
        c.razao_social, c.nome_fantasia, c.cnpj, c.municipio, c.cidade, c.uf,
        c.bairro, c.logradouro, c.cnae, c.atividade_principal,
      ].filter(Boolean).join(" ").toLowerCase();
      return hay.includes(term) || String(c.cnpj || "").replace(/\D/g, "").includes(term.replace(/\D/g, ""));
    });
  }, [clients, search, cityFilter, riskFilter, statusFilter]);

  const activeCount = clients.filter((c) => !c.archived_at).length;
  const archivedCount = clients.filter((c) => !!c.archived_at).length;
  const hasFilters = Boolean(search.trim() || cityFilter !== "todas" || riskFilter !== "todos" || statusFilter !== "ativos");
  const clearFilters = () => { setSearch(""); setCityFilter("todas"); setRiskFilter("todos"); setStatusFilter("ativos"); };

  return (
    <>
      <Header
        title="Clientes"
        subtitle="Abra um cliente para ver pendências, documentos, treinamentos, visitas e histórico em um só lugar."
        actions={canCreate ? (
          <Link to="/clientes/novo">
            <Button data-testid="new-client-btn" className="px-3 sm:px-4"><Plus className="size-4" /> <span className="hidden sm:inline">Novo cliente</span></Button>
          </Link>
        ) : null}
      />

      <div className="page-container space-y-5 sm:space-y-6" data-testid="clients-list-page">
        <div className="surface-panel space-y-4 p-4 sm:p-5">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div className="min-w-0">
              <div className="label-tech">Clientes cadastrados</div>
              <div className="mt-1 flex items-center gap-2">
                <Building2 className="size-4 text-primary" />
                <span className="text-sm font-medium">{activeCount} ativo(s){isAdmin && archivedCount > 0 ? ` · ${archivedCount} arquivado(s)` : ""}</span>
              </div>
            </div>
            <div className="relative w-full lg:max-w-xl">
              <Search className="absolute left-3.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
              <Input data-testid="clients-search-input" placeholder="Buscar cliente, CNPJ, cidade ou CNAE..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10" />
            </div>
          </div>
          <details className="rounded-xl border border-border/70 bg-muted/10 px-4 py-3">
            <summary className="cursor-pointer text-sm font-medium">Filtros</summary>
            <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-[160px_minmax(0,1fr)_180px_auto]">
              {isAdmin && (
                <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="h-10 rounded-xl border border-input bg-background px-3 text-sm outline-none transition focus:border-primary" aria-label="Filtrar clientes por status">
                  <option value="ativos">Ativos</option>
                  <option value="arquivados">Arquivados</option>
                  <option value="todos">Todos</option>
                </select>
              )}
              <select value={cityFilter} onChange={(e) => setCityFilter(e.target.value)} className="h-10 rounded-xl border border-input bg-background px-3 text-sm outline-none transition focus:border-primary" aria-label="Filtrar clientes por município">
                <option value="todas">Todos os municípios</option>
                {cities.map((city) => <option key={city} value={city}>{city}</option>)}
              </select>
              <select value={riskFilter} onChange={(e) => setRiskFilter(e.target.value)} className="h-10 rounded-xl border border-input bg-background px-3 text-sm outline-none transition focus:border-primary" aria-label="Filtrar clientes por grau de risco">
                <option value="todos">Todos os graus de risco</option>
                {risks.map((risk) => <option key={risk} value={risk}>Grau de risco {risk}</option>)}
              </select>
              <Button type="button" variant="outline" onClick={clearFilters} disabled={!hasFilters}><FilterX className="size-4" /> Limpar filtros</Button>
            </div>
          </details>
        </div>

        {loading && <div className="space-y-3">{[0, 1, 2, 3].map((i) => <div key={i} className="h-16 animate-pulse rounded-xl border border-border/70 bg-card/55" />)}</div>}

        {!loading && filtered.length === 0 && (
          <EmptyState
            testid="clients-empty"
            title={hasFilters ? "Nenhum cliente encontrado" : "Nenhum cliente cadastrado"}
            description={hasFilters ? "Ajuste os filtros ou limpe a busca." : "Cadastre o primeiro cliente para começar a gerar propostas e documentos."}
            action={canCreate && !hasFilters ? <Link to="/clientes/novo"><Button data-testid="empty-new-client-btn"><Plus className="size-4" /> Cadastrar cliente</Button></Link> : hasFilters ? <Button variant="outline" onClick={clearFilters}><FilterX className="size-4" /> Limpar filtros</Button> : null}
          />
        )}

        {!loading && filtered.length > 0 && (
          <>
            <div className="hidden overflow-x-auto rounded-2xl border border-border/80 bg-card/88 shadow-[0_1px_2px_hsl(var(--foreground)/0.03),0_18px_45px_hsl(var(--foreground)/0.035)] md:block">
              <table className="w-full text-sm" data-ui-table>
                <thead><tr><th className="px-5 py-3.5 text-left">Cliente</th><th className="px-5 py-3.5 text-left">CNPJ</th><th className="px-5 py-3.5 text-left">Município</th><th className="px-5 py-3.5 text-right">Funcionários</th><th className="px-5 py-3.5 text-right">GR</th><th className="px-5 py-3.5 text-right">Próxima ação</th></tr></thead>
                <tbody className="divide-y divide-border/70">
                  {filtered.map((c) => {
                    const archived = !!c.archived_at;
                    return (
                      <tr key={c.id} data-testid={`client-row-${c.id}`} className={archived ? "opacity-65" : ""}>
                        <td className="px-5 py-4 font-medium"><Link to={`/clientes/${c.id}/360`} className="transition hover:text-primary">{c.razao_social}</Link>{archived && <span className="ml-2 rounded-full bg-muted px-2 py-0.5 text-[10px] text-muted-foreground">Arquivado</span>}</td>
                        <td className="px-5 py-4 font-mono text-xs text-muted-foreground">{formatCNPJ(c.cnpj)}</td>
                        <td className="px-5 py-4 text-muted-foreground">{c.municipio || c.cidade || "-"}/{c.uf || "-"}</td>
                        <td className="px-5 py-4 text-right font-mono">{c.num_funcionarios ?? 0}</td>
                        <td className="px-5 py-4 text-right font-mono">{c.grau_risco || "—"}</td>
                        <td className="px-5 py-4">
                          <div className="flex justify-end gap-2">
                            <Link to={`/clientes/${c.id}/360`}><Button size="sm" data-testid={`open-client-${c.id}`}>Abrir cliente <ArrowRight className="size-3.5" /></Button></Link>
                            {!archived && <Link to={`/clientes/${c.id}`}><Button variant="outline" size="sm" data-testid={`edit-client-${c.id}`} title="Editar cadastro"><Pencil className="size-4" /></Button></Link>}
                            {isAdmin && !archived && <Button variant="ghost" size="sm" onClick={() => setConfirmArchive(c)} data-testid={`archive-client-${c.id}`} title="Arquivar cliente"><Archive className="size-4" /></Button>}
                            {isAdmin && archived && <Button variant="outline" size="sm" onClick={() => restoreClient(c)} data-testid={`restore-client-${c.id}`} title="Restaurar cliente"><RotateCcw className="size-4" /></Button>}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="grid gap-3 md:hidden">
              {filtered.map((c) => {
                const archived = !!c.archived_at;
                return (
                  <div key={c.id} className={`surface-panel p-4 ${archived ? "opacity-70" : ""}`} data-testid={`client-row-mobile-${c.id}`}>
                    <div className="min-w-0"><Link to={`/clientes/${c.id}/360`} className="block truncate font-medium tracking-[-0.01em]">{c.razao_social}</Link><div className="mt-1 font-mono text-[11px] text-muted-foreground">{formatCNPJ(c.cnpj)}{archived ? " · Arquivado" : ""}</div></div>
                    <div className="mt-4 grid grid-cols-3 gap-2 text-xs">
                      <div className="rounded-lg bg-muted/35 p-2.5"><MapPin className="mb-1 size-3.5 text-primary" /><div className="truncate text-muted-foreground">{c.municipio || c.cidade || "-"}/{c.uf || "-"}</div></div>
                      <div className="rounded-lg bg-muted/35 p-2.5"><Users className="mb-1 size-3.5 text-primary" /><div><span className="font-mono font-medium">{c.num_funcionarios ?? 0}</span> <span className="text-muted-foreground">func.</span></div></div>
                      <div className="rounded-lg bg-muted/35 p-2.5"><ShieldCheck className="mb-1 size-3.5 text-primary" /><div><span className="text-muted-foreground">GR</span> <span className="font-mono font-medium">{c.grau_risco || "—"}</span></div></div>
                    </div>
                    <div className="mt-4 flex gap-2">
                      <Link to={`/clientes/${c.id}/360`} className="flex-1"><Button className="w-full">Abrir cliente <ArrowRight className="size-3.5" /></Button></Link>
                      {!archived && <Link to={`/clientes/${c.id}`}><Button variant="outline" title="Editar cadastro"><Pencil className="size-4" /></Button></Link>}
                      {isAdmin && !archived && <Button variant="ghost" onClick={() => setConfirmArchive(c)} title="Arquivar cliente"><Archive className="size-4" /></Button>}
                      {isAdmin && archived && <Button variant="outline" onClick={() => restoreClient(c)} title="Restaurar cliente"><RotateCcw className="size-4" /></Button>}
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>

      <Dialog open={!!confirmArchive} onOpenChange={(o) => !o && setConfirmArchive(null)}>
        <DialogContent data-testid="archive-client-dialog">
          <DialogHeader>
            <DialogTitle>Arquivar cliente?</DialogTitle>
            <DialogDescription>O cliente <b>{confirmArchive?.razao_social}</b> sairá das listas ativas, mas propostas, treinamentos, documentos, ações e histórico serão preservados. Você poderá restaurá-lo depois.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmArchive(null)}>Cancelar</Button>
            <Button onClick={archiveClient} data-testid="confirm-archive-client-btn"><Archive className="size-4" /> Arquivar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
